import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AuthShell from '../../components/layout/AuthShell';
import Btn from '../../components/ui/Btn';
import { Avatar, Input, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import Logo from '../../components/ui/Logo';
import { ErrorBanner } from './SignInScreen';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { api } from '../../lib/api';
import { useIsMobile } from '../../lib/useBreakpoint';

/**
 * Accept Invite flow.
 * Triggered by a magic link Supabase sends from the invite email.
 *
 *   - Supabase processes the magic link, sets a session
 *   - We capture session.user.email (locked, read-only)
 *   - User enters first/last name + new password
 *   - We update the user (password + name metadata)
 *   - We update the team_members row for this user_id with the name
 *   - Redirect to /hub
 */
export default function AcceptInviteScreen() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [phase, setPhase] = useState('verifying'); // verifying | form | success | invalid
  const [userEmail, setUserEmail] = useState('');
  const [teamName, setTeamName] = useState(null); // optional, not always available
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pw, setPw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Captured once on first render so it survives Supabase's post-auth URL cleanup.
  const [inviteId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('invite'); }
    catch { return null; }
  });

  useEffect(() => {
    let timeoutId;
    let cancelled = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (cancelled) return;
        if (event === 'SIGNED_IN' && session?.user) {
          setUserEmail(session.user.email || '');
          // Pre-fill names if Supabase already has them on the user metadata
          const meta = session.user.user_metadata || {};
          if (meta.first_name) setFirstName(meta.first_name);
          if (meta.last_name)  setLastName(meta.last_name);
          // Try to look up the team they were invited to (best-effort, non-blocking)
          try {
            const { data: members } = await supabase
              .from('team_members')
              .select('team_id, teams(name)')
              .eq('user_id', session.user.id)
              .limit(1);
            if (!cancelled && members?.[0]?.teams?.name) {
              setTeamName(members[0].teams.name);
            }
          } catch {
            /* non-fatal */
          }
          setPhase('form');
        }
      }
    );

    // Fallback: also check immediately in case the event already fired
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data.session?.user) {
        setUserEmail(data.session.user.email || '');
        const meta = data.session.user.user_metadata || {};
        if (meta.first_name) setFirstName(meta.first_name);
        if (meta.last_name)  setLastName(meta.last_name);
        setPhase('form');
      } else {
        // Wait briefly for SIGNED_IN; if nothing arrives, mark invalid
        timeoutId = setTimeout(() => {
          if (!cancelled && phase === 'verifying') setPhase('invalid');
        }, 2500);
      }
    });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (pw.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Update password + name metadata on the auth user
      const { error: updateError } = await supabase.auth.updateUser({
        password: pw,
        data: { first_name: firstName.trim(), last_name: lastName.trim() },
      });
      if (updateError) throw updateError;

      // 2. Accept the specific invite they arrived through (service-role,
      //    consent-scoped to this one invite_id — never a sweep). Soft-fail:
      //    if it doesn't take, they can still accept manually in the Hub.
      if (inviteId) {
        try { await api.acceptInvite({ invite_id: inviteId }); }
        catch (e) { console.warn('Auto-accept failed, Hub fallback:', e.message); }
      }

      // 3. Sync the name onto the team_members row (now that it exists)
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const res = await fetch(
          `${SUPABASE_URL}/rest/v1/team_members?user_id=eq.${session.user.id}`,
          {
            method: 'PATCH',
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
              Prefer: 'return=minimal',
            },
            body: JSON.stringify({
              first_name: firstName.trim(),
              last_name: lastName.trim(),
            }),
          }
        );
        if (!res.ok) {
          // Don't fail the whole flow — the auth update already succeeded.
          console.warn('team_members PATCH failed:', await res.text());
        }
      }

      setPhase('success');
      setTimeout(() => navigate('/hub'), 1500);
    } catch (err) {
      console.error('Accept invite failed:', err);
      setError(err.message || 'Could not complete setup. Please try again.');
      setSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────
  if (phase === 'verifying') {
    return (
      <AuthShell>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          textAlign: 'center', gap: 14,
        }}>
          <Spinner size={22} color="var(--accent)" />
          <div style={{ fontSize: 14, color: 'var(--text-3)' }}>Verifying your invite…</div>
        </div>
      </AuthShell>
    );
  }

  if (phase === 'invalid') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
            background: 'var(--danger-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon.alert size={26} stroke="var(--danger)" />
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Invite link invalid</h1>
          <p style={{ fontSize: 13.5, color: 'var(--text-3)', marginBottom: 20 }}>
            This invite link is invalid or has expired. Ask whoever invited you to send a new one.
          </p>
          <Btn
            variant="primary"
            size="md"
            iconRight={Icon.arrowR}
            onClick={() => navigate('/sign-in')}
          >
            Go to sign in
          </Btn>
        </div>
      </AuthShell>
    );
  }

  if (phase === 'success') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, margin: '0 auto 16px',
            background: 'var(--accent-bg)',
            boxShadow: '0 0 0 8px rgba(61,199,91,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon.check size={32} stroke="var(--accent-600)" strokeWidth={2.5} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>You're all set!</h1>
          <p style={{ fontSize: 13.5, color: 'var(--text-3)' }}>Redirecting to your workspace…</p>
        </div>
      </AuthShell>
    );
  }

  // phase === 'form'
  return (
    <AuthShell>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <Avatar name={teamName || 'Workspace'} size={44} color="#0a0f2e" />
          <Icon.arrowR size={18} stroke="var(--text-muted)" />
          <Logo size={40} showWord={false} useFile />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 6 }}>
          {teamName ? `You're invited to ${teamName}` : 'Accept your invite'}
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--text-3)' }}>
          Set your password and finish your profile to get started.
        </p>
      </div>

      {error && <ErrorBanner message={error} style={{ marginBottom: 14 }} />}

      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
        Email
      </label>
      <Input
        icon={Icon.mail}
        value={userEmail}
        readOnly
        style={{ marginBottom: 12, opacity: 0.7, cursor: 'not-allowed' }}
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 10, marginBottom: 12,
      }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
            First name
          </label>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
            Last name
          </label>
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
        Set a password
      </label>
      <Input
        icon={Icon.lock}
        type="password"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        placeholder="At least 8 characters"
        autoComplete="new-password"
        style={{ marginBottom: 20 }}
        disabled={submitting}
      />

      <Btn
        variant="primary"
        size="lg"
        style={{ width: '100%' }}
        iconRight={submitting ? null : Icon.arrowR}
        onClick={submit}
        disabled={submitting}
      >
        {submitting ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Setting up…</> : 'Accept invite & continue'}
      </Btn>
    </AuthShell>
  );
}
